import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, Typography, Stack, Box, Grid, Divider, Link } from '@mui/material';

import { KioskFragment, KioskAdScheduleFragment } from '@occc/graphql';
import { formatDateTime } from '@occc-app/lib/date/date';
import { DateTimeFormat } from '@occc-app/lib/date/date.constants';

import { WEEKDAYS } from '../../../touchpoint.constants';
import { parseTimeString } from '../../../utils/date-formatters';

const formatDays = (days: string[]): string => {
  return days
    .map((day) => WEEKDAYS.find((weekday) => weekday.value === day)?.label)
    .filter(Boolean)
    .join(', ');
};

const formatTime = (time: string): string => {
  const date = parseTimeString(time);
  return date ? formatDateTime(date, DateTimeFormat.Time12h) : time;
};

type AdSetOffer = {
  id: string;
  name: string;
  url: string;
};

type AdSetDetailsCardProps = {
  offer?: AdSetOffer;
  kiosks?: KioskFragment[] | null;
  schedules?: KioskAdScheduleFragment[] | null;
};

export const AdSetDetailsCard = ({ offer, kiosks, schedules }: AdSetDetailsCardProps) => {
  return (
    <Card>
      <CardContent>
        <Grid container spacing={4} direction='row'>
          <Grid item xs={12} md={4}>
            <Stack>
              <Typography fontWeight={500} variant='body1' color='text.primary' sx={{ mb: 1 }}>
                Offer
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Title
              </Typography>
              {offer ? (
                <Link component={RouterLink} to={offer.url} color='primary' underline='hover'>
                  {offer.name}
                </Link>
              ) : (
                <Typography variant='body1'>No offer assigned</Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack>
              <Typography fontWeight={500} variant='body1' color='text.primary' sx={{ mb: 1 }}>
                Targeted Kiosks
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Kiosk ID
              </Typography>
              {kiosks && kiosks.length > 0 ? (
                <Stack spacing={0.5}>
                  {kiosks.map((kiosk) => (
                    <Typography key={kiosk.id} variant='body1'>
                      {kiosk.externalDisplayId || kiosk.id}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant='body1'>No kiosks targeted</Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack>
              {schedules && schedules.length > 0 ? (
                <Stack spacing={2} divider={<Divider />}>
                  {schedules.map((schedule, index) => (
                    <Box key={index}>
                      <Typography
                        fontWeight={500}
                        variant='body1'
                        color='text.primary'
                        sx={{ mb: 1 }}
                      >
                        Schedule {index + 1}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Available on
                      </Typography>
                      <Typography variant='body1' sx={{ mb: 1 }}>
                        {formatTime(schedule.startTime)} – {formatTime(schedule.endTime)}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        Availability window
                      </Typography>
                      <Typography variant='body1'>{formatDays(schedule.days)}</Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <>
                  <Typography fontWeight={500} variant='body1' color='text.primary' sx={{ mb: 1 }}>
                    Schedule
                  </Typography>
                  <Typography variant='body1'>No schedule set</Typography>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
